import { TextInput } from "@circleco/compass/components/TextInput";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import slugify from "slugify";

import { t } from "@/i18n-js/instance";

import { FormSection } from "./FormSection";
import { useResponsiveHorizontalFormLayout } from "./hooks/useResponsiveHorizontalFormLayout";
import type { NewSpaceGroupFormValues } from "./types";

const SLUG_OPTIONS = {
  replacement: "-",
  remove: /[*+~.;()'"!:@#]/g,
  lower: true,
};

const MAX_FIELD_LENGTH = 255;

export const BasicInfo = () => {
  const { control, watch, setValue, formState } =
    useFormContext<NewSpaceGroupFormValues>();
  const { horizontalLayoutInputProps, layout } =
    useResponsiveHorizontalFormLayout();
  const name = watch("name");
  const isNameTouched = Boolean(formState.dirtyFields.name);
  const isSlugTouched = Boolean(formState.dirtyFields.slug);

  useEffect(() => {
    if (name && !isSlugTouched && isNameTouched) {
      setValue("slug", slugify(name, SLUG_OPTIONS));
    }
  }, [name, isNameTouched, isSlugTouched, setValue]);

  return (
    <FormSection title={t("space_groups.form.create.general")}>
      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="name"
          rules={{
            required: t("space_groups.form.create.name_is_required"),
            maxLength: {
              value: MAX_FIELD_LENGTH,
              message: t("space_groups.form.create.name_max_length_error"),
            },
          }}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              {...horizontalLayoutInputProps}
              layout={layout}
              label={t("space_groups.form.create.space_group_name")}
              placeholder={t("space_groups.form.create.name_placeholder")}
              invalid={Boolean(fieldState.error)}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="slug"
          rules={{
            maxLength: {
              value: MAX_FIELD_LENGTH,
              message: t("space_groups.form.create.slug_max_length_error"),
            },
          }}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              onChange={event =>
                field.onChange(slugify(event.target.value, SLUG_OPTIONS))
              }
              {...horizontalLayoutInputProps}
              layout={layout}
              label={t("space_groups.form.create.slug")}
              labelProps={{ className: "opacity-80" }}
              labelTooltip={t("space_groups.form.create.slug_tooltip", {
                host: window.location.origin,
              })}
              placeholder={t("space_groups.form.create.slug_placeholder")}
              invalid={Boolean(fieldState.error)}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
    </FormSection>
  );
};
